import {
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Tag,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';

import { Schemas } from '@api-client/generated/types';
import countries from '@data/countries.json';

import { ObligationStatus, ObligationType, obligationStatusMap } from './const';
import {
  MONTH_NAMES,
  TAX_STATUS_NAMES,
  TAX_TYPE_NAMES,
  formatDateToString,
  formatTaxStatus,
} from './helpers';
import * as S from './styled';

type Tax = Schemas.AdminObligationResponseDto;
type TaxStatus = Schemas.AdminObligationResponseDto['status'];
type TaxPeriod = Schemas.AdminObligationResponseDto['period'];
type PeriodType = 'month' | 'quarter' | 'annual';

const TAX_TYPE_OPTIONS = Object.entries(TAX_TYPE_NAMES).map(
  ([value, label]) => ({ value, label })
);

const TAX_STATUS_OPTIONS = Object.entries(TAX_STATUS_NAMES).map(
  ([value, label]) => ({ value, label })
);

const PERIOD_TYPE_OPTIONS = [
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'annual', label: 'Year' },
];

// Generate month options (0-11)
const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: i,
  label: MONTH_NAMES[i],
}));

// Generate quarter options (0-3)
const QUARTER_OPTIONS = Array.from({ length: 4 }, (_, i) => ({
  value: i,
  label: `Q${i + 1}`,
}));

// Generate year options (current year - 1 to current year + 5)
const YEAR_OPTIONS = Array.from({ length: 10 }, (_, i) => {
  const year = new Date().getFullYear() - 5 + i;
  return { value: year, label: year.toString() };
});

type TaxEditModalProps = {
  tax?: Tax;
  isOpen: boolean;
  onClose: () => void;
  onCreate: (tax: Schemas.AdminCreateObligationDto) => void;
  onUpdate: (id: string, tax: Schemas.AdminUpdateObligationDto) => void;
  onDelete: (id: string) => void;
  isLoading?: boolean;
};

type FormType = Omit<
  Schemas.AdminObligationResponseDto,
  'dueDate' | 'paymentDueDate' | 'period'
> & {
  dueDate: Dayjs | null;
  paymentDueDate: Dayjs | null;
  periodType: PeriodType;
  month?: number;
  quarter?: number;
  year: number;
};

const TaxEditModal = (props: TaxEditModalProps) => {
  const [form] = Form.useForm<FormType>();
  const [loading, setLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<TaxStatus | undefined>(
    undefined
  );
  const [modal, contextHolder] = Modal.useModal();

  const isEditMode = !!props.tax;
  const modalTitle = useMemo(
    () =>
      isEditMode && props.tax
        ? `Edit ${TAX_TYPE_NAMES[props.tax.type]}`
        : 'Create New Tax',
    [isEditMode, props.tax]
  );

  const selectedTaxType = Form.useWatch('type', form);
  const periodType = Form.useWatch('periodType', form) || 'month';

  const filteredStatusOptions = useMemo(() => {
    if (!selectedTaxType) {
      return TAX_STATUS_OPTIONS;
    }

    const allowedStatuses = new Set(
      obligationStatusMap[selectedTaxType as ObligationType] || []
    );

    return Object.entries(TAX_STATUS_NAMES).reduce<
      { value: string; label: string }[]
    >((acc, [value, label]) => {
      if (allowedStatuses.has(value as ObligationStatus)) {
        acc.push({ value, label: `${acc.length + 1}. ${label}` });
      }
      return acc;
    }, []);
  }, [selectedTaxType]);

  useEffect(() => {
    if (props.isOpen) {
      setCurrentStatus(props.tax?.status);

      if (props.tax) {
        let extractedPeriodType: PeriodType = 'annual';
        let month: number | undefined;
        let quarter: number | undefined;
        let year = new Date().getFullYear();

        const taxPeriod = props.tax.period;
        if (taxPeriod) {
          if ('period' in taxPeriod) {
            extractedPeriodType = taxPeriod.period;
            year = taxPeriod.year;

            if (taxPeriod.period === 'month' && 'month' in taxPeriod) {
              month = taxPeriod.month;
            } else if (
              taxPeriod.period === 'quarter' &&
              'quarter' in taxPeriod
            ) {
              quarter = taxPeriod.quarter;
            }
          }
        }

        form.setFieldsValue({
          ...props.tax,
          periodType: extractedPeriodType,
          month,
          quarter,
          year,
          dueDate: props.tax.dueDate ? dayjs(props.tax.dueDate) : null,
          paymentDueDate: props.tax.paymentDueDate
            ? dayjs(props.tax.paymentDueDate)
            : null,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          periodType: 'annual',
          year: new Date().getFullYear(),
        });
      }
    }
  }, [props.tax, props.isOpen, form]);

  useEffect(() => {
    if (selectedTaxType && !isEditMode) {
      const allowedStatuses =
        obligationStatusMap[selectedTaxType as ObligationType] || [];
      if (allowedStatuses.length > 0) {
        const initialStatus = allowedStatuses[0];
        form.setFieldsValue({ status: initialStatus });
        setCurrentStatus(initialStatus);
      }
    }
  }, [selectedTaxType, isEditMode, form]);

  const handleStatusChange = (status: TaxStatus) => {
    setCurrentStatus(status);
  };

  const handlePeriodTypeChange = (e: Event) => {
    const newPeriodType = (e.target as HTMLSelectElement)?.value;

    if (newPeriodType === 'annual') {
      form.setFieldsValue({ month: undefined, quarter: undefined });
    } else if (newPeriodType === 'quarter') {
      form.setFieldsValue({ month: undefined });
    } else if (newPeriodType === 'month') {
      form.setFieldsValue({ quarter: undefined });
    }
  };

  const getCurrentStatusInfo = () => {
    if (!props.tax?.status) return null;
    return formatTaxStatus(props.tax.status);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const period: TaxPeriod = (() => {
        const { periodType, month, quarter, year } = values;

        if (periodType === 'month' && typeof month === 'number') {
          return { period: 'month', month, year };
        } else if (periodType === 'quarter' && typeof quarter === 'number') {
          return { period: 'quarter', quarter, year };
        } else {
          return { period: 'annual', year };
        }
      })();

      const formattedValues = {
        type: values.type,
        status: values.status,
        dueDate: formatDateToString(values.dueDate),
        paymentDueDate: values.paymentDueDate
          ? formatDateToString(values.paymentDueDate)
          : undefined,
        paymentDetails: values.paymentDetails || undefined,
        period,
        amount: values.amount,
        files: values.files,
      };

      if (isEditMode && props.tax?.id) {
        props.onUpdate(props.tax.id, formattedValues);
      } else {
        props.onCreate(formattedValues);
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    props.onClose();
  };

  const handleDelete = () => {
    modal.confirm({
      title: 'Are you sure you want to do this?',
      content: 'We will send an email to the client after you click "Ok".',
      onOk: () => {
        if (props.tax?.id) {
          props.onDelete(props.tax.id);
          props.onClose();
        }
      },
    });
  };

  return (
    <Modal
      title={
        <Flex align="center" gap={12}>
          <span>{modalTitle}</span>
          {isEditMode && props.tax?.status && (
            <Tag color={getCurrentStatusInfo()?.color}>
              {getCurrentStatusInfo()?.name}
            </Tag>
          )}
        </Flex>
      }
      open={props.isOpen}
      onCancel={handleCancel}
      width={700}
      footer={
        <Flex gap={16} justify="flex-end">
          {isEditMode && (
            <S.DeleteTaxButton
              type="secondary"
              danger
              onClick={handleDelete}
              disabled={props.isLoading}
            >
              Delete Tax
            </S.DeleteTaxButton>
          )}
          <Flex gap={8}>
            <Button
              key="cancel"
              type="secondary"
              onClick={handleCancel}
              disabled={props.isLoading}
            >
              Cancel
            </Button>
            <Button
              key="submit"
              type="primary"
              loading={loading || props.isLoading}
              onClick={handleSubmit}
              disabled={props.isLoading}
            >
              {isEditMode ? 'Save changes and send to client' : 'Create Tax'}
            </Button>
          </Flex>
        </Flex>
      }
    >
      <Form form={form} layout="vertical" disabled={props.isLoading}>
        <Form.Item
          name="type"
          label="Type"
          initialValue={TAX_TYPE_OPTIONS[0].value}
          rules={[{ required: true, message: 'Please select the tax type' }]}
        >
          <Select
            placeholder="Select tax type"
            disabled={isEditMode}
            options={TAX_TYPE_OPTIONS}
          />
        </Form.Item>

        <S.FormGroup>
          <S.ThreeColumns>
            <Form.Item
              name="periodType"
              label="Period Type"
              rules={[{ required: true, message: 'Please select period type' }]}
            >
              <Select
                options={PERIOD_TYPE_OPTIONS}
                onChange={handlePeriodTypeChange}
              />
            </Form.Item>

            <Form.Item
              name="year"
              label="Year"
              rules={[{ required: true, message: 'Please select year' }]}
            >
              <Select placeholder="Select year" options={YEAR_OPTIONS} />
            </Form.Item>

            {periodType === 'month' && (
              <Form.Item
                name="month"
                label="Month"
                rules={[{ required: true, message: 'Please select month' }]}
              >
                <Select placeholder="Select month" options={MONTH_OPTIONS} />
              </Form.Item>
            )}

            {periodType === 'quarter' && (
              <Form.Item
                name="quarter"
                label="Quarter"
                rules={[{ required: true, message: 'Please select quarter' }]}
              >
                <Select
                  placeholder="Select quarter"
                  options={QUARTER_OPTIONS}
                />
              </Form.Item>
            )}
          </S.ThreeColumns>
        </S.FormGroup>

        <S.FormGroup>
          <Form.Item
            name="status"
            label={isEditMode ? 'New Status' : 'Status'}
            rules={[
              { required: true, message: 'Please select the status' },
              {
                validator: (_, value) => {
                  if (!isEditMode) {
                    return Promise.resolve();
                  } else {
                    if (
                      value === ObligationStatus.WaitingForPayment ||
                      value === ObligationStatus.WaitingForClientApproval
                    ) {
                      return props.tax?.files.length
                        ? Promise.resolve()
                        : Promise.reject();
                    } else {
                      return Promise.resolve();
                    }
                  }
                },
                message:
                  'You need to attach files before changing to this status',
              },
            ]}
          >
            <Select
              placeholder="Select status"
              onChange={handleStatusChange}
              options={filteredStatusOptions}
              disabled={!isEditMode}
            />
          </Form.Item>

          <Form.Item
            name="dueDate"
            label="Due Date"
            rules={[{ required: true, message: 'Please select the due date' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
        </S.FormGroup>

        {currentStatus === 'waiting_for_payment' && (
          <S.FormGroup>
            <S.TwoColumns>
              <Form.Item
                name={['paymentDetails', 'countryCode']}
                label="Country"
                rules={[{ required: true, message: 'Please select country' }]}
                initialValue="LU"
              >
                <Select
                  placeholder="Select country"
                  options={countries.map((e) => ({
                    value: e.code,
                    label: e.name,
                  }))}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label?.toString() || '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item
                name={['paymentDetails', 'companyName']}
                label="Authority Name"
                rules={[
                  { required: true, message: 'Please enter authority name' },
                ]}
              >
                <Input placeholder="Enter authority name" />
              </Form.Item>
            </S.TwoColumns>

            <S.TwoColumns>
              <Form.Item
                name={['paymentDetails', 'iban']}
                label="IBAN"
                rules={[{ required: true, message: 'Please enter IBAN' }]}
              >
                <Input placeholder="Enter IBAN number" />
              </Form.Item>

              <Form.Item
                name={['paymentDetails', 'bic']}
                label="BIC"
                rules={[{ required: true, message: 'Please enter BICcode' }]}
              >
                <Input placeholder="Enter BIC/SWIFT code" />
              </Form.Item>
            </S.TwoColumns>

            <Form.Item
              name={['paymentDetails', 'reference']}
              label="Payment Reference"
              rules={[
                { required: true, message: 'Please enter payment reference' },
              ]}
            >
              <Input placeholder="Enter payment reference" />
            </Form.Item>

            <S.TwoColumns>
              <Form.Item
                name="paymentDueDate"
                label="Payment Due Date"
                rules={[
                  { required: true, message: 'Please enter payment due date' },
                ]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>

              <Form.Item
                name="amount"
                label="Amount"
                rules={[{ required: true, message: 'Please enter amount' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  addonBefore="€"
                  placeholder="Enter amount"
                />
              </Form.Item>
            </S.TwoColumns>
          </S.FormGroup>
        )}
      </Form>

      {contextHolder}
    </Modal>
  );
};

export default TaxEditModal;
